import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./App.css";

// Lazy load pages for better performance
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("./pages/RegisterPage").then((module) => ({
    default: module.RegisterPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((module) => ({
    default: module.default,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((module) => ({
    default: module.default,
  })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const TransactionsPage = lazy(() =>
  import("./pages/TransactionsPage").then((module) => ({
    default: module.TransactionsPage,
  })),
);
const MyTransactionsPage = lazy(() =>
  import("./pages/MyTransactionsPage").then((module) => ({
    default: module.MyTransactionsPage,
  })),
);
const CreateTransactionPage = lazy(() =>
  import("./pages/CreateTransactionPage").then((module) => ({
    default: module.CreateTransactionPage,
  })),
);
const HistoryPage = lazy(() =>
  import("./pages/HistoryPage").then((module) => ({
    default: module.HistoryPage,
  })),
);
const BankAccountsPage = lazy(() =>
  import("./pages/BankAccountsPage").then((module) => ({
    default: module.BankAccountsPage,
  })),
);
const UsersPage = lazy(() =>
  import("./pages/UsersPage").then((module) => ({ default: module.UsersPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const SolicitarPage = lazy(() =>
  import("./pages/SolicitarPage").then((module) => ({
    default: module.SolicitarPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const SolicitudDetallePage = lazy(() =>
  import("./pages/SolicitudDetallePage").then((module) => ({
    default: module.SolicitudDetallePage,
  })),
);
const AdminPage = lazy(() =>
  import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })),
);
const AdminLayout = lazy(() =>
  import("./layouts/AdminLayout").then((module) => ({
    default: module.AdminLayout,
  })),
);
const AdminDashboardPage = lazy(() =>
  import("./pages/admin/AdminDashboardPage").then((module) => ({
    default: module.AdminDashboardPage,
  })),
);
const BanksListPage = lazy(() =>
  import("./pages/admin/BanksListPage").then((module) => ({
    default: module.BanksListPage,
  })),
);
const BanksSyncPage = lazy(() =>
  import("./pages/admin/BanksSyncPage").then((module) => ({
    default: module.BanksSyncPage,
  })),
);
const AdminDebugPage = lazy(() =>
  import("./pages/admin/AdminDebugPage").then((module) => ({
    default: module.AdminDebugPage,
  })),
);
const SystemSettingsPage = lazy(() =>
  import("./pages/admin/SystemSettingsPage").then((module) => ({
    default: module.SystemSettingsPage,
  })),
);
const UsersListPage = lazy(() =>
  import("./pages/admin/UsersListPage").then((module) => ({
    default: module.UsersListPage,
  })),
);
const UsersRolesPage = lazy(() =>
  import("./pages/admin/UsersRolesPage").then((module) => ({
    default: module.UsersRolesPage,
  })),
);
const UsersActivityPage = lazy(() =>
  import("./pages/admin/UsersActivityPage").then((module) => ({
    default: module.UsersActivityPage,
  })),
);
const RequestsListPage = lazy(() =>
  import("./pages/admin/RequestsListPage").then((module) => ({
    default: module.RequestsListPage,
  })),
);
const PayOutsListPage = lazy(() =>
  import("./pages/admin/PayOutsListPage").then((module) => ({
    default: module.PayOutsListPage,
  })),
);
const RequestsPendingPage = lazy(() =>
  import("./pages/admin/RequestsPendingPage").then((module) => ({
    default: module.RequestsPendingPage,
  })),
);
const RequestsCompletedPage = lazy(() =>
  import("./pages/admin/RequestsCompletedPage").then((module) => ({
    default: module.RequestsCompletedPage,
  })),
);
const RequestsAnalyticsPage = lazy(() =>
  import("./pages/admin/RequestsAnalyticsPage").then((module) => ({
    default: module.RequestsAnalyticsPage,
  })),
);
const RequestsProcessingPage = lazy(() =>
  import("./pages/admin/RequestsProcessingPage").then((module) => ({
    default: module.RequestsProcessingPage,
  })),
);
const RequestsFailedPage = lazy(() =>
  import("./pages/admin/RequestsFailedPage").then((module) => ({
    default: module.RequestsFailedPage,
  })),
);
const RequestDetailPage = lazy(() =>
  import("./pages/admin/RequestDetailPage").then((module) => ({
    default: module.RequestDetailPage,
  })),
);
const PayOutDetailPage = lazy(() =>
  import("./pages/admin/PayOutDetailPage").then((module) => ({
    default: module.PayOutDetailPage,
  })),
);
const PayInsPage = lazy(() =>
  import("./pages/admin/PayInsPage").then((module) => ({
    default: module.PayInsPage,
  })),
);
const PayoutBalancePage = lazy(() =>
  import("./pages/admin/PayoutBalancePage").then((module) => ({
    default: module.PayoutBalancePage,
  })),
);
const AlertsPage = lazy(() =>
  import("./pages/admin/AlertsPage").then((module) => ({
    default: module.AlertsPage,
  })),
);
const AdminDebugEndpoints = lazy(() =>
  import("./pages/admin/AdminDebugEndpoints").then((module) => ({
    default: module.AdminDebugEndpoints,
  })),
);
const DebugPage = lazy(() =>
  import("./pages/DebugPage").then((module) => ({ default: module.DebugPage })),
);
const DevPayouts = lazy(() =>
  import("./pages/dev/DevPayouts").then((module) => ({
    default: module.DevPayouts,
  })),
);
const ClientProfilePage = lazy(() =>
  import("./pages/ClientProfilePage").then((module) => ({
    default: module.ClientProfilePage,
  })),
);
const CashoutRequestsPage = lazy(() =>
  import("./pages/CashoutRequestsPage").then((module) => ({
    default: module.CashoutRequestsPage,
  })),
);
const PaymentSuccessPage = lazy(() =>
  import("./pages/PaymentSuccessPage").then((module) => ({
    default: module.PaymentSuccessPage,
  })),
);
const PaymentErrorPage = lazy(() =>
  import("./pages/PaymentErrorPage").then((module) => ({
    default: module.PaymentErrorPage,
  })),
);
const UnauthorizedPage = lazy(() =>
  import("./pages/UnauthorizedPage").then((module) => ({
    default: module.UnauthorizedPage,
  })),
);
const ForbiddenPage = lazy(() =>
  import("./pages/ForbiddenPage").then((module) => ({
    default: module.ForbiddenPage,
  })),
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((module) => ({
    default: module.NotFoundPage,
  })),
);

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600">Cargando página...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: "#10b981",
                    secondary: "#fff",
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: "#ef4444",
                    secondary: "#fff",
                  },
                },
              }}
            />
            <div className="app">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route
                    path="/payment-success"
                    element={<PaymentSuccessPage />}
                  />
                  <Route path="/payment-error" element={<PaymentErrorPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/403" element={<ForbiddenPage />} />
                  {/* Admin routes with AdminLayout */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute roles="admin">
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to="/admin/dashboard" replace />}
                    />
                    <Route path="dashboard" element={<AdminDashboardPage />} />
                    <Route path="debug" element={<AdminDebugPage />} />
                    <Route
                      path="debug-endpoints"
                      element={<AdminDebugEndpoints />}
                    />
                    <Route path="settings" element={<SystemSettingsPage />} />

                    {/* Banks routes */}
                    <Route path="banks/list" element={<BanksListPage />} />
                    <Route path="banks/sync" element={<BanksSyncPage />} />
                    <Route path="banks/enabled" element={<BanksListPage />} />

                    {/* Users routes */}
                    <Route path="users/list" element={<UsersListPage />} />
                    <Route path="users/roles" element={<UsersRolesPage />} />
                    <Route
                      path="users/activity"
                      element={<UsersActivityPage />}
                    />

                    {/* Requests routes */}
                    <Route
                      path="requests/list"
                      element={<RequestsListPage />}
                    />
                    <Route path="requests/all" element={<RequestsListPage />} />
                    <Route
                      path="requests/pending"
                      element={<RequestsPendingPage />}
                    />
                    <Route
                      path="requests/completed"
                      element={<RequestsCompletedPage />}
                    />
                    <Route
                      path="requests/processing"
                      element={<RequestsProcessingPage />}
                    />
                    <Route
                      path="requests/failed"
                      element={<RequestsFailedPage />}
                    />
                    <Route
                      path="requests/analytics"
                      element={<RequestsAnalyticsPage />}
                    />
                    <Route
                      path="requests/:id"
                      element={<RequestDetailPage />}
                    />

                    {/* Payments routes */}
                    <Route
                      path="payments/payouts"
                      element={<PayOutsListPage />}
                    />
                    <Route
                      path="payments/payouts/:id"
                      element={<PayOutDetailPage />}
                    />
                    <Route path="payments/payins" element={<PayInsPage />} />
                    <Route
                      path="payments/balance"
                      element={<PayoutBalancePage />}
                    />
                    <Route path="payments/alerts" element={<AlertsPage />} />
                  </Route>
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="transactions" element={<TransactionsPage />} />
                    <Route
                      path="my-transactions"
                      element={<MyTransactionsPage />}
                    />
                    <Route
                      path="create-transaction"
                      element={<CreateTransactionPage />}
                    />
                    <Route path="history" element={<HistoryPage />} />
                    <Route
                      path="bank-accounts"
                      element={
                        <ProtectedRoute roles={["user", "client"]}>
                          <BankAccountsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <ProtectedRoute roles="admin">
                          <UsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="my-transactions"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <MyTransactionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="create-transaction"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <CreateTransactionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="solicitar" element={<SolicitarPage />} />
                    <Route path="onboarding" element={<OnboardingPage />} />
                    <Route
                      path="solicitudes/:id"
                      element={<SolicitudDetallePage />}
                    />
                    {/* Legacy admin route */}
                    <Route
                      path="admin/legacy"
                      element={
                        <ProtectedRoute roles="admin">
                          <AdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="debug"
                      element={
                        <ProtectedRoute roles={["admin", "operator"]}>
                          <DebugPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="dev/payouts"
                      element={
                        <ProtectedRoute roles="admin">
                          <DevPayouts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="client/profile"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <ClientProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="client/requests"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <CashoutRequestsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="solicitar"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <SolicitarPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="onboarding"
                      element={
                        <ProtectedRoute roles={["client", "user"]}>
                          <OnboardingPage />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
